import {useEffect} from 'react';

import {BannerCodes} from '../../consts';
import {useAnalytics} from '../../hooks/AdFox/useAnalytics';
import {useAutorefresh} from '../../hooks/AdFox/useAutorefresh';
import {useDivAvailabilityTracker} from '../../hooks/AdFox/useDivAvailabilityTracker';
import {useStats} from '../../hooks/AdFox/useStats';
import {useBannerContainerId} from '../../hooks/useBannerContainerId';
import {useRequestContextScriptLoad} from '../../hooks/useRequestContextScriptLoad';
import {BannerProps} from '../../types';
import {combineAndExtendCallbacks} from '../../utils/combineAndExtendCallbacks';

export const AdFoxBanner = ({
  adType,
  placement,
  position,
  onRender,
  onError,
  onNoAds,
  bannerContainerClassName,
  adFoxConfig,
}: BannerProps) => {
  const isScriptLoaded = useRequestContextScriptLoad('adfox') === 'loaded';

  const code = BannerCodes[placement];
  const containerId = useBannerContainerId(placement, position);

  useDivAvailabilityTracker({containerId, code});

  const createAnalyticsCallbacks = useAnalytics({adType, position});
  const {callbacksFactory: createAutorefreshCallbacks, getRefreshNumber} =
    useAutorefresh({
      containerId,
      isFloorAd: placement === 'floor_ad_banner',
      code,
      config: adFoxConfig?.autorefresh,
      enabled: adFoxConfig?.enabled,
    });

  const createStatsCallbacks = useStats({getRefreshNumber});

  const config = adFoxConfig;

  useEffect(() => {
    if (!isScriptLoaded) {
      return;
    }

    const {callbacks, cleanup} = combineAndExtendCallbacks({
      containerId,
      code,
      factories: [
        createAnalyticsCallbacks,
        createStatsCallbacks,
        createAutorefreshCallbacks,
      ],
      callback: {
        onRender,
        onError,
        onStub: onNoAds,
      },
    });

    window.yaContextCb?.push(() => {
      window.Ya?.adfoxCode?.create({
        ...code,
        containerId,
        ...(config.lazyLoad && {lazyLoad: config.lazyLoad}),
        ...callbacks,
      });
    });

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded]);

  return <div id={containerId} className={bannerContainerClassName}></div>;
};
